import { Box, Chip, Stack, Typography } from "@mui/material";
import {
  Controls,
  Handle,
  MarkerType,
  type NodeProps,
  Position,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
} from "@xyflow/react";
import dagre from "dagre";
import type React from "react";
import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router";
import { STATUS_COLORS, theme } from "../../theme/theme";
import { clickableProps } from "../../utils/keyboard";

import "@xyflow/react/dist/style.css";

// A single check rendered as a graph node.
export interface CheckGraphNode {
  id: string;
  name: string;
  status?: string;
}

export interface CheckGraphEdge {
  source: string;
  target: string;
}

interface CheckDependencyGraphProps {
  // The schedule dependency graph: { "root": [...ids], "<id>": [...dependentIds] }.
  dependencyGraph: Record<string, string[]> | null | undefined;
  // Checks to render — id + display name.
  checks: { id: string; name: string }[];
  // Optional run status per check id, for coloring nodes in a run context.
  statusByCheckId?: Record<string, string>;
  height?: number | string;
}

// Map a run status to node colors. Mirrors the passed/failed/skipped/pending buckets.
function statusColors(status?: string): { border: string; bg: string } {
  switch (status) {
    case "passed":
      return { border: STATUS_COLORS.success.text, bg: STATUS_COLORS.success.bg };
    case "failed":
    case "error":
    case "timed_out":
      return { border: STATUS_COLORS.error.text, bg: STATUS_COLORS.error.bg };
    case "skipped":
    case "cancelled":
      return { border: STATUS_COLORS.default.border, bg: STATUS_COLORS.default.bg };
    case "running":
    case "pending":
      return { border: STATUS_COLORS.info.text, bg: STATUS_COLORS.info.bg };
    default:
      return {
        border: STATUS_COLORS.default.border,
        bg: theme.palette.background.paper,
      };
  }
}

const NODE_WIDTH = 200;
const NODE_HEIGHT = 80;

const CheckNode = ({ data }: NodeProps) => {
  const navigate = useNavigate();
  const { label, status, checkId } = data as unknown as {
    label: string;
    status?: string;
    checkId: string;
  };
  const { border, bg } = statusColors(status);

  const handleClick = () => {
    navigate(`/checks/${checkId}`);
  };

  return (
    <Box
      border={2}
      borderColor={border}
      bgcolor={bg}
      borderRadius={2}
      p={1}
      minWidth={NODE_WIDTH - 20}
      {...clickableProps(handleClick)}
      onClick={handleClick}
      sx={{
        cursor: "pointer",
        "&:hover": {
          boxShadow: 2,
          transform: "translateY(-1px)",
          transition: "all 0.2s ease-in-out",
        },
      }}
    >
      <Handle type="target" position={Position.Left} />
      <Stack direction="column" alignItems="center" spacing={0.25}>
        <Chip
          label="API"
          size="small"
          sx={{
            height: 16,
            fontSize: "0.6rem",
            backgroundColor: STATUS_COLORS.info.bg,
            color: STATUS_COLORS.info.text,
          }}
        />
        <Typography
          variant="body2"
          fontWeight={600}
          align="center"
          fontSize={12}
          maxWidth={NODE_WIDTH - 30}
          noWrap
        >
          {label}
        </Typography>
        {status && (
          <Typography variant="caption" color="text.secondary" fontSize={9}>
            {status}
          </Typography>
        )}
      </Stack>
      <Handle type="source" position={Position.Right} />
    </Box>
  );
};

const nodeTypes = { check: CheckNode };

// Build edges from the dependency graph adjacency.
// Graph value = the IDs of checks that depend on the key, so key -> dependent.
function buildEdges(
  dependencyGraph: Record<string, string[]> | null | undefined,
  checkIds: Set<string>,
): CheckGraphEdge[] {
  if (!dependencyGraph) return [];
  const edges: CheckGraphEdge[] = [];
  for (const [from, dependents] of Object.entries(dependencyGraph)) {
    if (from === "root") continue;
    if (!checkIds.has(from)) continue;
    for (const dependent of dependents) {
      if (checkIds.has(dependent)) {
        edges.push({ source: from, target: dependent });
      }
    }
  }
  return edges;
}

function layoutPositions(
  nodes: CheckGraphNode[],
  edges: CheckGraphEdge[],
): Record<string, { x: number; y: number }> {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: "LR", nodesep: 60, ranksep: 140, marginx: 40, marginy: 40 });
  nodes.forEach((n) => {
    g.setNode(n.id, { width: NODE_WIDTH, height: NODE_HEIGHT });
  });
  edges.forEach((e) => {
    g.setEdge(e.source, e.target);
  });
  dagre.layout(g);
  const positions: Record<string, { x: number; y: number }> = {};
  nodes.forEach((n) => {
    const pos = g.node(n.id);
    positions[n.id] = { x: pos.x - NODE_WIDTH / 2, y: pos.y - NODE_HEIGHT / 2 };
  });
  return positions;
}

const CheckDependencyGraphInner: React.FC<CheckDependencyGraphProps> = ({
  dependencyGraph,
  checks,
  statusByCheckId,
  height = 480,
}) => {
  const { fitView } = useReactFlow();

  const checkIds = useMemo(() => new Set(checks.map((c) => c.id)), [checks]);

  const graphNodes = useMemo<CheckGraphNode[]>(
    () =>
      checks.map((c) => ({
        id: c.id,
        name: c.name,
        status: statusByCheckId?.[c.id],
      })),
    [checks, statusByCheckId],
  );

  const graphEdges = useMemo(
    () => buildEdges(dependencyGraph, checkIds),
    [dependencyGraph, checkIds],
  );

  const positions = useMemo(
    () => layoutPositions(graphNodes, graphEdges),
    [graphNodes, graphEdges],
  );

  const flowNodes = useMemo(
    () =>
      graphNodes.map((n) => ({
        id: n.id,
        type: "check",
        position: positions[n.id] ?? { x: 0, y: 0 },
        data: { label: n.name, status: n.status, checkId: n.id },
      })),
    [graphNodes, positions],
  );

  const flowEdges = useMemo(
    () =>
      graphEdges.map((e) => ({
        id: `${e.source}-${e.target}`,
        source: e.source,
        target: e.target,
        type: "bezier",
        markerEnd: { type: MarkerType.Arrow, color: STATUS_COLORS.default.border },
        style: { stroke: STATUS_COLORS.default.border, strokeWidth: 1.5 },
      })),
    [graphEdges],
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      fitView({ padding: 0.2, duration: 300, maxZoom: 1.5, minZoom: 0.1 });
    }, 100);
    return () => clearTimeout(timer);
  }, [fitView]);

  if (checks.length === 0) {
    return (
      <Typography color="text.secondary" sx={{ p: 2 }}>
        No checks to display.
      </Typography>
    );
  }

  return (
    <Box
      width="100%"
      height={height}
      border={1}
      borderColor="divider"
      borderRadius={2}
      sx={{ overflow: "hidden" }}
    >
      <ReactFlow
        key={`check-graph-${flowNodes.length}`}
        nodes={flowNodes}
        edges={flowEdges}
        minZoom={0.1}
        maxZoom={4}
        nodeTypes={nodeTypes}
        proOptions={{ hideAttribution: true }}
      >
        <Controls showZoom showFitView showInteractive />
      </ReactFlow>
    </Box>
  );
};

const CheckDependencyGraph: React.FC<CheckDependencyGraphProps> = (props) => (
  <ReactFlowProvider>
    <CheckDependencyGraphInner {...props} />
  </ReactFlowProvider>
);

export default CheckDependencyGraph;
